<template>
    <div class='overview'
         v-if="student">
        <section class="is-medium is-bold">
            <div class="container">
                <div class="column">
                    <div class="subtitle is-spaced">
                        <nav class="breadcrumb"
                             aria-label="breadcrumbs">
                            <ul>
                                <router-link tag="li"
                                             active-class="is-active"
                                             :to="{ name: 'administration.students' }"
                                             exact>
                                    <router-link :to="{ name: 'administration.students' }"
                                                 exact>Studerende</router-link>
                                </router-link>

                                <router-link tag="li"
                                             active-class="is-active"
                                             :to="{ name: 'administration.students.detail', params: { id: student.id } }"
                                             exact>
                                    <router-link :to="{ name: 'administration.students.detail', params: { id: student.id } }"
                                                 exact>{{ student.name }}</router-link>
                                </router-link>
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>
        </section>

        <section class="section">
            <div class="columns is-multiline">
                <div class="column is-6 is-12-touch">
                    <div class="card box is-radiusless is-shadowless">
                        <div class="card-body">
                            <div class="columns">
                                <div class="column is-4">
                                    <avatar :account="student"
                                            :size="200" />
                                </div>
                                <div class="column is-8">
                                    <h1 class="title is-spaced is-4">{{ student.name }}</h1>

                                    <div class="buttons">
                                        <button type="button"
                                                class="button is-small"
                                                @click="showInfoEditModal = true">
                                            <b-icon icon="pencil"
                                                    size="is-small" />
                                            <span>{{ $t('global.actions.edit') }}</span>
                                        </button>

                                        <info-edit-modal :student="student"
                                                         v-if="showInfoEditModal"
                                                         @close="showInfoEditModal = false"
                                                         @updated="(newStudent) => this.student = newStudent" />

                                        <router-link class="button is-small is-info"
                                                     :to="{ name: 'students.profile', params: { slug: student.slug } }">
                                            <b-icon icon="account"
                                                    size="is-small" />
                                            <span>Se profil</span>
                                        </router-link>

                                        <user-activation-toggle-button :user="student.user" />
                                    </div>

                                    <ul>
                                        <li><b>Email:</b> {{ student.user.email }}</li>
                                        <li><b>Fødselsdato:</b> {{ student.date_of_birth | formatDate }}</li>
                                        <li><b>Telefonnummer:</b> {{ student.phone_number }}</li>
                                    </ul>

                                    <br>
                                    <p class="is-small">Gennemsnitlig vurdering</p>
                                    <stars :average="student.average_rating" />

                                    <br />
                                    <ul>
                                        <li>
                                            <strong>Noter:</strong>
                                            <br />
                                            <template v-for="line in splitNotes">
                                                {{ line }} <br />
                                            </template>

                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="column is-6 is-12-touch">
                    <div class="card box is-radiusless is-shadowless">
                        <div class="card-body">
                            <p class="subtitle">
                                Færdigheder & Niveau &nbsp;
                                <a class="is-size-7"
                                   href="#"
                                   @click.prevent="showSkillsLevelEditModal = true">
                                    {{ $t('global.actions.edit') }}
                                </a>
                            </p>

                            <skills-level-edit-modal :student="student"
                                                     v-if="showSkillsLevelEditModal"
                                                     @close="showSkillsLevelEditModal = false" />

                            <p class="field">
                                <b>Niveau: </b>{{ $t(`levels.${student.level.name}.name`) }} <level-icon
                                            :level="student.level"
                                            size="is-small" />
                            </p>


                            <p class="field"><b>Færdigheder:</b></p>

                            <b-field grouped
                                     group-multiline
                                     v-if="student.level && student.skills.length">
                                <skill-tag v-for="skill in student.skills"
                                           :key="skill.id"
                                           :skill="skill"
                                           :pay-grade="paygrade(skill, student.level)" />
                            </b-field>

                            <p v-else>Ingen færdigheder</p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="columns is-multiline">
                <div class="column is-6 is-12-touch">
                    <div class="card box is-radiusless is-shadowless">
                        <div class="card-body">
                            <p class="subtitle">
                                Profiltekster &nbsp;<a class="is-size-7"
                                   href="#"
                                   @click.prevent="showProfileTextEditModal = true">Rediger</a>
                            </p>

                            <profile-text-edit-modal v-if="showProfileTextEditModal"
                                                     :student="student"
                                                     @close="showProfileTextEditModal = false"
                                                     @updated="(newStudent) => this.student = newStudent" />

                            <div>
                                <p><strong>Kort tekst:</strong></p>
                                <p v-if="student.short">{{ student.short }}</p>
                                <p v-else
                                   class="is-italic">Der er ingen kort tekst.</p>
                                <br>
                            </div>

                            <div>
                                <p><strong>Lang tekst</strong></p>
                                <div class="content"
                                     v-html="student.about"></div>
                            </div>
                        </div>
                    </div>
                </div>


                <div class="column is-6 is-12-touch">
                    <div class="card box is-radiusless is-shadowless">
                        <div class="card-body">
                            <p class="subtitle">
                                Projekter
                            </p>

                            <ul class="projects-list">
                                <li v-for="(project, index) in student.projects"
                                    :key="index">
                                    {{ project.name }} <br>

                                    <strong>Kunde: </strong>{{ project.customer.name }} <br>

                                    <strong>Status: </strong>{{ $t(`projects.status.${project.status}`) }}
                                </li>

                                <p v-if="!student.projects.length">Denne studerende har ikke nogen projekter endnu.</p>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div class="columns is-multiline">
                <div class="column is-6 is-12-touch">
                    <div class="card box is-radiusless is-shadowless">
                        <div class="card-body">
                            <p class="subtitle">Udtalelser</p>

                            <ratings-list :student="student" />
                            <br>

                            <button class="button is-primary is-radiusless"
                                    @click="showCreateRatingModal = true">
                                Tilføj udtalelse
                            </button>

                            <create-rating-modal v-if="showCreateRatingModal"
                                                 :student="student"
                                                 @close="showCreateRatingModal = false"
                                                 @created="load()" />
                        </div>
                    </div>
                </div>

                <div class="column is-6 is-12-touch">
                    <div class="card box is-radiusless is-shadowless">
                        <div class="card-body">
                            <p class="subtitle">Referencer</p>

                            <div class='columns is-multiline'>
                                <reference class='column is-half is-full-mobile'
                                           v-for="reference in student.references"
                                           :reference="reference"
                                           :key="reference.id"
                                           :student="student" />
                            </div>

                            <create-reference v-if="student.references.length < 6"
                                              :student-id="student.id" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<style lang="scss" scoped>
@import '../../../../global/styles/variables';

.projects-list {
    li {
        padding-bottom: .5rem;
    }
}
</style>

<script>
import Avatar from '@/taskflex/users/components/Avatar';
import CreateRatingModal from '@/taskflex/administration/components/students/CreateRatingModal';
import CreateReference from '@/taskflex/students/components/profile/references/CreateReference';
import InfoEditModal from '@/taskflex/administration/components/students/InfoEditModal';
import SkillsLevelEditModal from '@/taskflex/administration/components/students/SkillsLevelEditModal';
import LevelIcon from '@/taskflex/levels/components/LevelIcon';
import ProfileTextEditModal from '@/taskflex/administration/components/students/ProfileTextEditModal';
import RatingsList from '@/taskflex/students/components/profile/ratings/RatingsList';
import Reference from '@/taskflex/students/components/profile/references/Reference';
import SkillTag from '@/taskflex/skills/components/SkillTag';
import Stars from '@/taskflex/students/components/profile/ratings/Stars';
import StudentService from '@/taskflex/students/services/StudentService';
import UserActivationToggleButton from '@/taskflex/administration/components/UserActivationToggleButton';

export default {
    data() {
        return {
            student: null,
            loading: false,
            showInfoEditModal: false,
            showProfileTextEditModal: false,
            showCreateRatingModal: false,
            showSkillsLevelEditModal: false,
        };
    },

    computed: {
        id() {
            const { id = null } = this.$route.params;

            return id;
        },
        splitNotes() {
            if (!this.student.notes) return [];
            return this.student.notes.split('\n');
        },
    },

    mounted() {
        this.load();

        this.$eventHub.$on('student.detail.reload', this.load);
    },

    beforeDestroy() {
        this.$eventHub.$off('student.detail.reload', this.load);
    },

    methods: {
        paygrade(skill, level) {
            const { paygrades } = skill;

            return paygrades.find(paygrade => paygrade.level_id === level.id);
        },

        load() {
            this.loading = true;

            const options = {
                includes: [
                    'user',
                    'level',
                    'skills',
                    'skills.paygrades',
                    'projects',
                    'projects.customer',
                    'references',
                    'ratings',
                ],
            };

            StudentService.find(this.id, options, ({ data }) => {
                this.student = data;

                this.loading = false;
            });
        },
    },

    components: {
        Avatar,
        LevelIcon,
        SkillTag,
        UserActivationToggleButton,
        InfoEditModal,
        SkillsLevelEditModal,
        ProfileTextEditModal,
        Reference,
        CreateRatingModal,
        CreateReference,
        Stars,
        RatingsList,
    },
};
</script>



// WEBPACK FOOTER //
// src/taskflex/administration/pages/students/Detail.vue